/**
 * Selectors - State Access Helpers
 * 
 * This module provides selector functions for efficiently accessing
 * and computing derived state values. Selectors can be memoized
 * for performance optimization.
 * 
 * @module store/selectors
 */

/**
 * Basic State Selectors
 */

// UI Selectors
export const getCurrentTab = (state) => state.ui?.currentTab || 'strategic-framework';
export const getIsLoading = (state) => state.ui?.loading || false;
export const getLoadingMessage = (state) => state.ui?.loadingMessage || '';
export const getModalOpen = (state) => state.ui?.modalOpen;
export const getModalProps = (state) => state.ui?.modalProps || {};
export const getSidebarVisible = (state) => state.ui?.sidebarVisible !== false;
export const getTheme = (state) => state.ui?.theme || 'light';
export const getToasts = (state) => state.ui?.toasts || [];

// User Selectors
export const getApiKey = (state) => state.user?.apiKey;
export const getSelectedModel = (state) => state.user?.model || 'gpt-4o';
export const getUserPreferences = (state) => state.user?.preferences || {};
export const getIsDebugEnabled = (state) => state.user?.preferences?.enableDebug || false;
export const getIsAnalyticsEnabled = (state) => state.user?.preferences?.enableAnalytics || false;
export const getAutoSaveEnabled = (state) => state.user?.preferences?.autoSave !== false;

// Initiative Selectors
export const getAllInitiatives = (state) => state.initiatives || [];
export const getInitiativeCount = (state) => (state.initiatives || []).length;
export const getSelectedInitiativeId = (state) => state.selection?.selectedInitiative;

export const getInitiativeById = (state, id) => {
  return (state.initiatives || []).find(initiative => initiative.id === id);
};

export const getSelectedInitiative = (state) => {
  const selectedId = getSelectedInitiativeId(state);
  return selectedId ? getInitiativeById(state, selectedId) : null;
};

// Chat Selectors
export const getChatMessages = (state) => state.chat?.messages || [];
export const getChatMessageCount = (state) => (state.chat?.messages || []).length;
export const getIsTyping = (state) => state.chat?.typing || false;
export const getConversationDepth = (state) => state.chat?.conversationDepth || 0;
export const getConversationContext = (state) => state.chat?.context;

export const getLastChatMessage = (state) => {
  const messages = getChatMessages(state);
  return messages.length > 0 ? messages[messages.length - 1] : null;
};

export const getChatMessagesBySender = (state, sender) => {
  return getChatMessages(state).filter(message => message.sender === sender);
};

// Strategy Selectors
export const getUploadedStrategy = (state) => state.strategies?.uploaded;
export const getEmergentStrategy = (state) => state.strategies?.emergent;
export const getCurrentStrategy = (state) => state.strategies?.current;
export const getHasStrategy = (state) => !!(getCurrentStrategy(state));

export const getStrategyType = (state) => {
  const current = getCurrentStrategy(state);
  const uploaded = getUploadedStrategy(state);
  const emergent = getEmergentStrategy(state);
  
  if (current === uploaded) return 'uploaded';
  if (current === emergent) return 'emergent';
  return 'none';
};

// Portfolio Selectors
export const getPortfolioAnalysis = (state) => state.portfolio?.analysis;
export const getPortfolioConflicts = (state) => state.portfolio?.conflicts || [];
export const getPortfolioLastAnalyzed = (state) => state.portfolio?.lastAnalyzed;
export const getPortfolioStatus = (state) => state.portfolio?.status;

export const getHasPortfolioAnalysis = (state) => !!getPortfolioAnalysis(state);
export const getPortfolioConflictCount = (state) => getPortfolioConflicts(state).length;

// System Selectors
export const getSystemVersion = (state) => state.system?.version || '5.1.1';
export const getIsInitialized = (state) => state.system?.initialized || false;
export const getSystemErrors = (state) => state.system?.errors || [];
export const getErrorCount = (state) => getSystemErrors(state).length;
export const getLastError = (state) => {
  const errors = getSystemErrors(state);
  return errors.length > 0 ? errors[errors.length - 1] : null;
};

/**
 * Computed Selectors (Derived State)
 */

// Initiative Analytics
export const getInitiativeStats = (state) => {
  const initiatives = getAllInitiatives(state);
  
  const stats = {
    total: initiatives.length,
    byStatus: {},
    byPriority: {},
    byCategory: {}
  };
  
  initiatives.forEach(initiative => {
    // Count by status
    const status = initiative.status || 'draft';
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    
    // Count by priority
    const priority = initiative.priority || 'medium';
    stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1;
    
    // Count by category
    const category = initiative.category || 'uncategorized';
    stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
  });
  
  return stats;
};

export const getInitiativesByStatus = (state, status) => {
  return getAllInitiatives(state).filter(initiative => 
    (initiative.status || 'draft') === status
  );
};

export const getInitiativesByPriority = (state, priority) => {
  return getAllInitiatives(state).filter(initiative => 
    (initiative.priority || 'medium') === priority
  );
};

export const getHighPriorityInitiatives = (state) => {
  return getInitiativesByPriority(state, 'high');
};

export const getActiveInitiatives = (state) => {
  return getAllInitiatives(state).filter(initiative => 
    ['active', 'in-progress', 'ongoing'].includes(initiative.status)
  );
};

export const getRecentInitiatives = (state, days = 7) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  
  return getAllInitiatives(state).filter(initiative => {
    const createdAt = new Date(initiative.createdAt || initiative.timestamp);
    return createdAt >= cutoff;
  });
};

// Chat Analytics
export const getChatStats = (state) => {
  const messages = getChatMessages(state);
  
  const stats = {
    total: messages.length,
    byType: {},
    avgResponseTime: 0,
    lastActivity: null
  };
  
  let totalResponseTime = 0;
  let responseCount = 0;
  
  messages.forEach((message, index) => {
    // Count by sender
    const sender = message.sender || 'unknown';
    stats.byType[sender] = (stats.byType[sender] || 0) + 1;
    
    // Calculate response times
    if (index > 0 && message.sender === 'ai') {
      const prevMessage = messages[index - 1];
      if (prevMessage.sender === 'user') {
        const responseTime = new Date(message.timestamp) - new Date(prevMessage.timestamp);
        totalResponseTime += responseTime;
        responseCount++;
      }
    }
    
    // Track last activity
    if (!stats.lastActivity || new Date(message.timestamp) > new Date(stats.lastActivity)) {
      stats.lastActivity = message.timestamp;
    }
  });
  
  if (responseCount > 0) {
    stats.avgResponseTime = totalResponseTime / responseCount;
  }
  
  return stats;
};

export const getRecentChatMessages = (state, minutes = 30) => {
  const cutoff = new Date();
  cutoff.setMinutes(cutoff.getMinutes() - minutes);
  
  return getChatMessages(state).filter(message => {
    const timestamp = new Date(message.timestamp);
    return timestamp >= cutoff;
  });
};

// Application Health
export const getApplicationHealth = (state) => {
  const health = {
    status: 'healthy',
    issues: [],
    score: 100
  };
  
  // Check for API key
  if (!getApiKey(state)) {
    health.issues.push('No API key configured');
    health.score -= 30;
  }
  
  // Check for errors
  const errorCount = getErrorCount(state);
  if (errorCount > 0) {
    health.issues.push(`${errorCount} system errors`);
    health.score -= Math.min(errorCount * 10, 40);
  }
  
  // Check initialization
  if (!getIsInitialized(state)) {
    health.issues.push('System not fully initialized');
    health.score -= 20;
  }
  
  // Determine overall status
  if (health.score >= 80) {
    health.status = 'healthy';
  } else if (health.score >= 60) {
    health.status = 'warning';
  } else {
    health.status = 'critical';
  }
  
  return health;
};

// Search and Filter Helpers
export const searchInitiatives = (state, searchTerm) => {
  if (!searchTerm) return getAllInitiatives(state);
  
  const term = searchTerm.toLowerCase();
  return getAllInitiatives(state).filter(initiative => {
    return (
      (initiative.title || '').toLowerCase().includes(term) ||
      (initiative.description || '').toLowerCase().includes(term) ||
      (initiative.category || '').toLowerCase().includes(term) ||
      (initiative.status || '').toLowerCase().includes(term)
    );
  });
};

export const filterInitiatives = (state, filters = {}) => {
  let initiatives = getAllInitiatives(state);
  
  if (filters.status) {
    initiatives = initiatives.filter(i => i.status === filters.status);
  }
  
  if (filters.priority) {
    initiatives = initiatives.filter(i => i.priority === filters.priority);
  }
  
  if (filters.category) {
    initiatives = initiatives.filter(i => i.category === filters.category);
  }
  
  if (filters.search) {
    const term = filters.search.toLowerCase();
    initiatives = initiatives.filter(i => 
      (i.title || '').toLowerCase().includes(term) ||
      (i.description || '').toLowerCase().includes(term)
    );
  }
  
  return initiatives;
};

// UI State Helpers
export const getUIState = (state) => ({
  currentTab: getCurrentTab(state),
  loading: getIsLoading(state),
  loadingMessage: getLoadingMessage(state),
  modalOpen: getModalOpen(state),
  modalProps: getModalProps(state),
  sidebarVisible: getSidebarVisible(state),
  theme: getTheme(state),
  toasts: getToasts(state)
});

export const getBusyState = (state) => ({
  loading: getIsLoading(state),
  typing: getIsTyping(state),
  hasModal: !!getModalOpen(state),
  toastCount: getToasts(state).length
});

/**
 * Memoization Utilities
 */

// Simple memoization for expensive selectors
const memoCache = new Map();

export function memoizeSelector(selector, keyFn = (state) => JSON.stringify(state)) {
  return (state, ...args) => {
    const key = keyFn(state, ...args);
    
    if (memoCache.has(key)) {
      return memoCache.get(key);
    }
    
    const result = selector(state, ...args);
    memoCache.set(key, result);
    
    // Clean cache periodically
    if (memoCache.size > 100) {
      const firstKey = memoCache.keys().next().value;
      memoCache.delete(firstKey);
    }
    
    return result;
  };
}

// Clear memoization cache
export function clearSelectorCache() {
  memoCache.clear();
}

/**
 * Complex Computed Selectors (Memoized)
 */

export const getMemoizedInitiativeStats = memoizeSelector(
  getInitiativeStats,
  (state) => JSON.stringify(state.initiatives)
);

export const getMemoizedChatStats = memoizeSelector(
  getChatStats,
  (state) => JSON.stringify(state.chat?.messages)
);

export const getMemoizedApplicationHealth = memoizeSelector(
  getApplicationHealth,
  (state) => `${state.user?.apiKey || 'none'}-${state.system?.errors?.length || 0}-${state.system?.initialized}`
);

// Export for debugging in development
if (import.meta.env?.DEV) {
  window.StoreSelectors = {
    // Basic selectors
    getCurrentTab,
    getIsLoading,
    getAllInitiatives,
    getChatMessages,
    getApiKey,
    getCurrentStrategy,
    getPortfolioAnalysis,
    getSystemVersion,
    
    // Computed selectors
    getInitiativeStats,
    getChatStats,
    getApplicationHealth,
    
    // Search/filter
    searchInitiatives,
    filterInitiatives,
    
    // Utilities
    memoizeSelector,
    clearSelectorCache
  };
  console.log('🔧 Store selectors available at window.StoreSelectors');
}
